import { useEffect, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { businessConfig } from "@/config/business.config";

/**
 * Site masthead. Three rows on desktop:
 *  1. dark utility bar (region · family-run · fully insured)
 *  2. large logo (left) + click-to-call block + social circles
 *  3. primary nav + "Get a Quote"
 *
 * On scroll past ~12px the header slims down (utility bar hides, logo shrinks)
 * — full masthead on initial load. Below 980px it collapses to logo +
 * hamburger with a drop-down menu.
 */
const navLinks = [
  { to: "/", label: "Home", end: true },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/service-areas", label: "Service Areas" },
  { to: "/#reviews", label: "Reviews", hash: true },
  { to: "/contact", label: "Contact" },
];

const social = businessConfig.online.social;
const facebookUrl = (social.facebook as string) !== "TO CONFIRM" ? social.facebook : "#";
const instagramUrl = (social.instagram as string) !== "TO CONFIRM" ? social.instagram : "#";
const googleUrl = businessConfig.online.googleBusinessProfileUrl;

function FacebookIcon() {
  return (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="#fff" aria-hidden="true">
      <path d="M13.5 21v-8h2.6l.4-3h-3V8.2c0-.9.3-1.5 1.6-1.5H16.6V4c-.3 0-1.2-.1-2.2-.1-2.2 0-3.7 1.3-3.7 3.8V10H8v3h2.7v8h2.8z" />
    </svg>
  );
}
function InstagramIcon() {
  return (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" aria-hidden="true">
      <rect x="3.5" y="3.5" width="17" height="17" rx="5" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="17.2" cy="6.8" r="1.1" fill="#fff" stroke="none" />
    </svg>
  );
}
function PhoneIcon({ size = 23, fill = "#fff" }: { size?: number; fill?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={fill} aria-hidden="true">
      <path d="M6.6 10.8a15 15 0 0 0 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1A17 17 0 0 1 3 4c0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.2.2 2.4.6 3.6.1.4 0 .8-.3 1l-2.2 2.2Z" />
    </svg>
  );
}

export function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setIsMobileOpen(false);
  }, [location.pathname, location.hash]);

  // While the mobile menu is open, hide the sticky bottom bar (the open menu
  // already carries call + quote actions).
  useEffect(() => {
    document.body.classList.toggle("menu-open", isMobileOpen);
    return () => document.body.classList.remove("menu-open");
  }, [isMobileOpen]);

  return (
    <header className={`hdr ${isScrolled ? "is-scrolled" : ""}`}>
      <div className="hdr__utility">
        <div className="container hdr__utility-inner">
          <span className="hdr__utility-left">
            <span className="hdr__diamond" aria-hidden="true" />
            <span className="hdr__utility-full">
              {businessConfig.serviceRegion.primaryLabel.toUpperCase()} · FAMILY-RUN · FULLY INSURED
            </span>
            <span className="hdr__utility-short">MELBOURNE · FAMILY-RUN · INSURED</span>
          </span>
          <span className="hdr__utility-right">FREE QUOTES · COMPETITIVE PRICING</span>
        </div>
      </div>

      <div className="container hdr__bar">
        <Link to="/" aria-label={`${businessConfig.identity.tradingName}, home`} className="hdr__logo">
          <img src="/brand/logo.png" alt={businessConfig.identity.tradingName} className="hdr__logo-img" />
        </Link>

        <div className="hdr__right" data-desktop>
          <div className="hdr__top-row">
            <a href={businessConfig.contact.phoneHref} className="hdr__call">
              <span className="hdr__call-ico">
                <PhoneIcon size={23} />
              </span>
              <span className="hdr__call-text">
                <span className="hdr__call-label">Call for a free quote</span>
                <span className="hdr__call-number">{businessConfig.contact.phoneDisplay}</span>
              </span>
            </a>
            <div className="hdr__social">
              <a href={facebookUrl} aria-label="Facebook" target="_blank" rel="noopener" className="hdr__social-btn">
                <FacebookIcon />
              </a>
              <a href={googleUrl} aria-label="Google reviews" target="_blank" rel="noopener" className="hdr__social-btn hdr__social-btn--g">
                G
              </a>
              <a href={instagramUrl} aria-label="Instagram" target="_blank" rel="noopener" className="hdr__social-btn">
                <InstagramIcon />
              </a>
            </div>
          </div>

          <nav aria-label="Primary" className="hdr__nav">
            {navLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.end}
                className={({ isActive }) => `hdr__link ${isActive && !link.hash ? "is-active" : ""}`}
              >
                {link.label}
              </NavLink>
            ))}
            <Link to="/contact" className="hdr__quote">
              Get a Quote
            </Link>
          </nav>
        </div>

        <button
          type="button"
          aria-expanded={isMobileOpen}
          aria-controls="mobile-nav"
          aria-label={isMobileOpen ? "Close menu" : "Open menu"}
          onClick={() => setIsMobileOpen((v) => !v)}
          className="hdr__toggle"
          data-mobile
        >
          <svg width="22" height="16" viewBox="0 0 22 16" fill="none" aria-hidden="true">
            {isMobileOpen ? (
              <path d="M1 1l20 14M1 15L21 1" stroke="currentColor" strokeWidth="2" />
            ) : (
              <path d="M0 1h22M0 8h22M0 15h22" stroke="currentColor" strokeWidth="2" />
            )}
          </svg>
        </button>
      </div>

      {isMobileOpen && (
        <nav id="mobile-nav" aria-label="Mobile" className="hdr__mobile">
          <div className="container">
            {navLinks.map((link) => (
              <NavLink key={link.to} to={link.to} end={link.end} className="hdr__mobile-link">
                {link.label}
                <span aria-hidden="true">→</span>
              </NavLink>
            ))}
            <Link to="/contact" className="hdr__mobile-cta hdr__mobile-cta--orange">
              Get a Quote
            </Link>
            <a href={businessConfig.contact.phoneHref} className="hdr__mobile-cta hdr__mobile-cta--dark">
              Call {businessConfig.contact.phoneDisplay}
            </a>
            <div className="hdr__mobile-social">
              <a href={facebookUrl} aria-label="Facebook" target="_blank" rel="noopener" className="hdr__social-btn">
                <FacebookIcon />
              </a>
              <a href={googleUrl} aria-label="Google reviews" target="_blank" rel="noopener" className="hdr__social-btn hdr__social-btn--g">
                G
              </a>
              <a href={instagramUrl} aria-label="Instagram" target="_blank" rel="noopener" className="hdr__social-btn">
                <InstagramIcon />
              </a>
            </div>
          </div>
        </nav>
      )}

      <style>{`
        .hdr {
          position: sticky;
          top: 0;
          z-index: 100;
          background: var(--white);
          border-bottom: 2px solid var(--graphite);
          font-family: var(--font-body);
        }

        /* Utility bar */
        .hdr__utility { background: var(--graphite); color: var(--muted-on-dark); }
        .hdr__utility-inner {
          display: flex; justify-content: space-between; align-items: center; gap: 16px;
          padding-block: 9px;
          font-family: var(--font-mono); font-size: 13px; line-height: 1;
          overflow: hidden;
        }
        .hdr__utility-left { display: inline-flex; align-items: center; gap: 8px; white-space: nowrap; }
        .hdr__diamond { width: 7px; height: 7px; background: var(--orange); transform: rotate(45deg); display: inline-block; flex-shrink: 0; }
        .hdr__utility-right { white-space: nowrap; }
        .hdr__utility-short { display: none; }
        @media (max-width: 720px) { .hdr__utility-right { display: none; } }
        @media (max-width: 560px) {
          .hdr__utility-full { display: none; }
          .hdr__utility-short { display: inline; }
        }

        /* Main bar */
        .hdr__bar { display: flex; align-items: center; justify-content: space-between; gap: 24px; padding-block: 14px; }
        .hdr__logo { flex-shrink: 0; display: inline-flex; align-items: center; margin-left: clamp(0px, 4vw, 90px); }
        .hdr__logo-img { height: 120px; width: auto; transition: height var(--duration-base) var(--ease-standard); }

        .hdr__right { flex: 1; display: flex; flex-direction: column; align-items: flex-end; gap: 14px; min-width: 0; }
        .hdr__top-row { display: flex; align-items: center; gap: 22px; }

        .hdr__call { display: inline-flex; align-items: center; gap: 12px; color: var(--graphite); text-decoration: none; }
        .hdr__call:hover { color: var(--orange-hover); }
        .hdr__call-ico {
          width: 46px; height: 46px; border-radius: 50%; background: var(--orange);
          display: flex; align-items: center; justify-content: center; flex-shrink: 0;
        }
        .hdr__call-text { display: flex; flex-direction: column; line-height: 1.1; }
        .hdr__call-label { font-family: var(--font-mono); font-size: 10.5px; letter-spacing: 0.06em; text-transform: uppercase; color: var(--muted-text); }
        .hdr__call-number { font-family: var(--font-display); font-size: 29px; font-weight: 900; letter-spacing: 0.01em; color: var(--wordmark-navy); transition: font-size var(--duration-base) var(--ease-standard); }

        .hdr__social { display: flex; align-items: center; gap: 9px; }
        .hdr__social-btn {
          width: 40px; height: 40px; border-radius: 50%; background: var(--orange);
          display: flex; align-items: center; justify-content: center; text-decoration: none;
          color: #fff; font-family: var(--font-display); font-weight: 900; font-size: 19px;
          transition: background var(--duration-fast) var(--ease-standard);
        }
        .hdr__social-btn:hover { background: var(--orange-hover); }

        .hdr__nav { display: flex; align-items: center; gap: 2px; }
        .hdr__link {
          display: inline-block; padding: 8px 11px;
          font-family: var(--font-display); font-weight: 800; font-size: 14.5px;
          letter-spacing: 0.02em; text-transform: uppercase; white-space: nowrap;
          color: var(--graphite); text-decoration: none;
          transition: color var(--duration-fast) var(--ease-standard);
        }
        .hdr__link:hover, .hdr__link.is-active { color: var(--orange); }
        .hdr__quote {
          margin-left: 10px; display: inline-flex; align-items: center;
          background: var(--orange); color: #fff;
          font-family: var(--font-display); font-weight: 800; font-size: 14.5px;
          letter-spacing: 0.02em; text-transform: uppercase; white-space: nowrap;
          padding: 11px 22px; text-decoration: none;
          transition: background var(--duration-fast) var(--ease-standard);
        }
        .hdr__quote:hover { background: var(--orange-hover); }

        /* Slim-on-scroll */
        .hdr.is-scrolled .hdr__utility { display: none; }
        .hdr.is-scrolled .hdr__logo-img { height: 76px; }
        .hdr.is-scrolled .hdr__call-number { font-size: 24px; }
        .hdr.is-scrolled .hdr__bar { padding-block: 10px; }

        /* Toggle + mobile */
        .hdr__toggle {
          display: none; background: none; border: 2px solid var(--graphite);
          color: var(--graphite); padding: 8px 10px; line-height: 0; cursor: pointer;
        }
        .hdr__mobile { border-top: 1px solid var(--border-strong); padding: 12px 0 20px; background: var(--white); }
        .hdr__mobile-link {
          display: flex; justify-content: space-between; align-items: center;
          padding: 14px 4px; font-family: var(--font-display); font-weight: 700; font-size: 18px;
          text-transform: uppercase; color: var(--graphite); text-decoration: none;
          border-bottom: 1px solid var(--border-subtle);
        }
        .hdr__mobile-link span { color: var(--orange); }
        .hdr__mobile-link.active { color: var(--orange); }
        .hdr__mobile-cta {
          display: flex; justify-content: center; align-items: center; gap: 10px; margin-top: 10px;
          color: #fff; font-family: var(--font-display); font-weight: 900; font-size: 18px;
          text-transform: uppercase; padding: 15px; text-decoration: none;
        }
        .hdr__mobile-cta--orange { margin-top: 16px; background: var(--orange); }
        .hdr__mobile-cta--dark { background: var(--graphite); }
        .hdr__mobile-social { display: flex; justify-content: center; gap: 12px; margin-top: 16px; }

        @media (min-width: 981px) { [data-mobile] { display: none !important; } }
        @media (max-width: 980px) {
          [data-desktop] { display: none !important; }
          .hdr__toggle { display: inline-flex; }
          .hdr__logo { margin-left: 0; }
          .hdr__logo-img { height: 72px; }
          .hdr.is-scrolled .hdr__logo-img { height: 60px; }
        }
      `}</style>
    </header>
  );
}
